export interface ApiResponse<T> {
  data: T | undefined;
  success: boolean;
  message?: string;
}

export interface ApiClientConfig {
  baseUrl: string;
  forgeRockAccessToken: string;
  defaultTimeout?: number;
}

type Methods = 'GET' | 'POST';

export const createApiClient = (config: ApiClientConfig) => {
  const defaultConfig = {
    defaultTimeout: 10_000,
    ...config
  };

  const callApi = async <TRequest, TResponse>(
    method: Methods,
    endpoint: string,
    bodyData?: TRequest,
    options?: { timeout?: number }
  ): Promise<ApiResponse<TResponse>> => {
    const url = `${defaultConfig.baseUrl}${endpoint}`;
    const timeoutMs = options?.timeout || defaultConfig.defaultTimeout!;

    try {
      const controller = new AbortController();

      /* istanbul ignore next */
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const request: RequestInit = {
        method,
        credentials: 'include' as RequestCredentials,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${defaultConfig.forgeRockAccessToken}`
        },
        signal: controller.signal
      };

      if (bodyData) {
        request.body = JSON.stringify(bodyData);
      }

      const response = await fetch(url, request);
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return {
        data: result as TResponse,
        success: true
      };
    } catch (error) {
      return {
        data: undefined as TResponse,
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  const get = async <TResponse>(endpoint: string, options?: { timeout?: number }): Promise<ApiResponse<TResponse>> =>
    callApi<undefined, TResponse>('GET', endpoint, undefined, options);

  const post = async <TRequest, TResponse>(endpoint: string, data: TRequest, options?: { timeout?: number }): Promise<ApiResponse<TResponse>> =>
    callApi<TRequest, TResponse>('POST', endpoint, data, options);

  return {
    get,
    post
  };
};
